import { Lifestyle } from "@/clients/models/Lifestyle";
import { LifestyleOption } from "@/clients/models/LifestyleOption";
import { MealTime } from "@/clients/models/MealTime";
import { QueryKeys } from "@/constants/query-keys";
import { setProfileContext } from "@/utils/sentry/tools";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useGetLifestyle } from "../../../../hooks/requests/lifestyle/use-get-lifestyle";
import { useGetLifestyleOptions } from "../../../../hooks/requests/lifestyle/use-get-lifestyle-options";
import { useUpdateLifestyle } from "../../../../hooks/requests/lifestyle/use-update-lifestyle";
import { useToast } from "../../../../hooks/ui/use-toast";

type LifeStyleKeys = keyof Lifestyle;
const LifeStyleQueryMapping: Record<MealTime, Partial<LifeStyleKeys>> = {
  [MealTime.BREAKFAST]: "breakfast_preferences",
  [MealTime.LUNCH]: "lunch_preferences",
  [MealTime.DINNER]: "evening_preferences",
  [MealTime.SNACK]: "snack_preferences",
} as const;

export const useLifeStyle = (mealTime: MealTime, onSuccess: () => void) => {
  const queryClient = useQueryClient();
  const { lifestyle, isLoading: isLoadingLifestyle } = useGetLifestyle();
  const { updateLifestyle, isUpdating } = useUpdateLifestyle();
  const { options, isLoading: isLoadingOptions } = useGetLifestyleOptions(mealTime);
  const { showError } = useToast();
  const [selectedPreference, setSelectedPreference] = useState<string[]>([]);

  useEffect(() => {
    if (lifestyle && options) {
      const mealTimeKey = LifeStyleQueryMapping[mealTime] as keyof Lifestyle;
      const preferences = lifestyle[mealTimeKey];
      const validOptions = options?.lifestyleOptionsData?.map((o) => o.value);
      setSelectedPreference(
        preferences?.filter((p) => validOptions?.includes(p)).map((p) => p) ?? [],
      );
    }
  }, [lifestyle, mealTime, options]);

  const handlePreferenceSelect = useCallback(
    (preference: LifestyleOption) => {
      return () => {
        setSelectedPreference((prev) => {
          if (prev.includes(preference.value)) {
            return prev.filter((p) => p !== preference.value);
          } else {
            return [...prev, preference.value];
          }
        });
      };
    },
    [setSelectedPreference],
  );

  const handleSubmit = () => {
    if (!selectedPreference || selectedPreference.length === 0) {
      showError("Please select at least one preference");
      return;
    }
    const dataBody: Lifestyle = lifestyle ?? {
      breakfast_preferences: [],
      lunch_preferences: [],
      evening_preferences: [],
      snack_preferences: [],
    };
    const keyToUpdate = LifeStyleQueryMapping[mealTime];
    if (!keyToUpdate) return;
    dataBody[keyToUpdate] = selectedPreference;

    updateLifestyle(dataBody, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QueryKeys.LIFESTYLE] });
        queryClient.invalidateQueries({
          predicate(query) {
            return (
              query.queryKey.includes(QueryKeys.PLAN_ALTERNATIVE_FILTER_OPTIONS) &&
              query.queryKey.includes(mealTime)
            );
          },
        });
        setProfileContext(QueryKeys.LIFESTYLE, dataBody);
        onSuccess?.();
      },
      onError: (error) => {
        showError(error.message);
      },
    });
  };

  return {
    selectedPreference,
    isLoading: isLoadingLifestyle || isLoadingOptions,
    handlePreferenceSelect,
    handleSubmit,
    isUpdating,
    options,
  };
};
